import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import FluidBackground from './components/FluidBackground';
import SpaceCanvas from './components/SpaceCanvas';
import CookieConsentBanner from './components/CookieConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import VideoModal from './components/VideoModal';
import ChatWidget from './components/ChatWidget';
import './App.css';

// Pages
import HomePage from './pages/HomePage'
import AboutPage from './pages/AboutPage'
import ServicesPage from './pages/ServicesPage'
import PortfolioPage from './pages/PortfolioPage'
import BlogPage from './pages/BlogPage'
import BlogPostPage from './pages/BlogPostPage'
import ContactPage from './pages/ContactPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import DataDeletionPage from './pages/DataDeletionPage'
import SEOToolPage from './pages/SEOToolPage'
import WebDesignerCenturionPage from './pages/WebDesignerCenturionPage'
import NotFound from './pages/NotFound'

// Scroll to top on route change
function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        // Immediate scroll to top without smooth behavior
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant'
        });
        
        // Also ensure document body is scrolled to top
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        // Track SPA page views in GA4
        if (window.gtag) {
            window.gtag('event', 'page_view', {
                page_path: pathname,
                page_location: window.location.href,
            });
        }
    }, [pathname]);

    return null;
}

function AppContent() {
    const location = useLocation();

    useEffect(() => {
        // Initialize GSAP animations
        gsap.registerPlugin(ScrollTrigger);
        
        // Only clear ScrollTriggers that aren't pinned (don't kill ServicesScroll)
        ScrollTrigger.getAll().forEach(trigger => {
            if (!trigger.vars.pin) {
                trigger.kill();
            }
        });
        
        // Small delay to ensure DOM is updated after route change
        const timeoutId = setTimeout(() => {
            // Animate hero elements immediately without scroll trigger (do this first)
            const heroElements = document.querySelectorAll('.hero .title-h1, .hero .subtitle-h2, .hero .description');
            heroElements.forEach((el, index) => {
                gsap.fromTo(el,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        delay: 0.3 + (index * 0.2)
                    }
                );
            });
            
            // Animate other elements on scroll
            const elements = document.querySelectorAll('.title-h1, .title-h2, .subtitle-h2, .subtitle-h3, .description');
            elements.forEach(el => {
                // Skip if element is inside services-scroll-section
                if (el.closest('.services-scroll-section')) {
                    // Set initial visibility for elements in scroll section
                    gsap.set(el, { opacity: 1, y: 0 });
                    return;
                }
                
                // Skip if element is in hero section (already animated above)
                if (el.closest('.hero')) {
                    return;
                }
                
                gsap.fromTo(el,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        scrollTrigger: {
                            trigger: el,
                            start: 'top 90%',
                            toggleActions: 'play none none reverse'
                        }
                    }
                );
            });
            
            // Refresh ScrollTrigger to recalculate positions
            ScrollTrigger.refresh();
        }, 100);

        // Smooth scroll for anchor links
        const handleAnchorClick = (e) => {
            const href = e.currentTarget.getAttribute('href');
            if (href && href.startsWith('#')) {
                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    const headerHeight = document.querySelector('.sticky-header')?.offsetHeight || 80;
                    const targetPosition = target.offsetTop - headerHeight - 20;
                    
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        };

        const anchorLinks = document.querySelectorAll('a[href^="#"]');
        anchorLinks.forEach(link => {
            link.addEventListener('click', handleAnchorClick);
        });

        return () => {
            clearTimeout(timeoutId);
            anchorLinks.forEach(link => {
                link.removeEventListener('click', handleAnchorClick);
            });
        };
    }, [location.pathname]);

    return (
        <>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/data-deletion" element={<DataDeletionPage />} />
                <Route path="/seo-audit" element={<SEOToolPage />} />
                <Route path="/web-designer-centurion" element={<WebDesignerCenturionPage />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <HelmetProvider>
                <div className="App">
                    <FluidBackground />
                    <SpaceCanvas />
                    <CookieConsentBanner />
                    <VideoModal />
                    <ChatWidget />
                    <div className="content">
                        <Router>
                            <AppContent />
                        </Router>
                    </div>
                </div>
            </HelmetProvider>
        </ErrorBoundary>
    );
}

export default App;